import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import { useAuth } from '../../context/AuthContext';
import { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import { employeeAsync } from '../performance-actions/ActionFormDrawer';
import { Grid } from '../performance-operations/styles';
import { usePanel } from '../performance-operations/config';
import AiCenterKpis from './components/AiCenterKpis';
import FindingsTab from './tab-pages/FindingsTab';
import ActionsTab from './tab-pages/ActionsTab';
import ManagementTab from './tab-pages/ManagementTab';
import { KPI_CODES, fetchPeriods, fetchSummary } from './config';

// P7a — E15 AI bulguları ve aksiyon merkezi (/performance/ai-center). İK ve yöneticiler için kanıtlı bulguları inceleme, görev verme
// ve sonucu izleme alanı. Üst: M56/M57/M43/M44; sekmeler: Bulgu Kuyruğu, Aksiyon Geçmişi, Yönetim (yalnız İK: AI taslak kalite ve
// süre göstergeleri). Dönem/sekme/sorumlu süzgeci URL'de (geri dönüşte korunur). Görünürlük sunucuda: yönetici yalnız kendi
// alt ağacındaki yönetici kapsamlı bulguları (İK'ya özel kurallar hariç) görür. Taslak değerlendirme ve görüşme hazırlığı
// işlevleri formlara ve E14 profiline gömülüdür (analiz §18: kullanıcı merkeze gitmeden çalışabilir).
// Tek kaydırma: üst şerit sabit, sekme gövdesi ölçülen yükseklikte; yalnız DataTable satırları kayar.
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Header = styled.div`
  flex-shrink: 0;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  width: ${({ $w }) => $w || '240px'};
  max-width: 100%;
  @media (max-width: 768px) { width: 100%; }
`;

const TabBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Note = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

export default function PerformanceAiCenterPage() {
  const { t } = useTranslation();
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const periodId = query.get('period') || '';
  const owner = query.get('owner') || null;
  const requestedTab = query.get('tab') || 'findings';
  const tab = requestedTab === 'management' && !isHr ? 'findings' : requestedTab;

  const setQuery = useCallback((patch) => {
    const p = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
    history.replace({ pathname: location.pathname, search: p.toString() ? `?${p.toString()}` : '' });
  }, [history, location.pathname, location.search]);

  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);
  // Varsayılan dönem: en yeni dönem (URL'de yoksa ya da artık yoksa).
  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!periodId || !periods.some((p) => p.id === periodId)) setQuery({ period: latestPeriod(periods).id });
  }, [periodsLoaded, periods, periodId, setQuery]);

  const [refreshKey, setRefreshKey] = useState(0);
  const onChanged = useCallback(() => setRefreshKey((k) => k + 1), []);
  const summary = usePanel(() => fetchSummary(periodId), [periodId, refreshKey], !!periodId);

  // Sekme gövdesinin ölçülen yüksekliği (DataTable satır alanı bunu aşmaz).
  const bodyRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setBodyHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const tabs = [
    { key: 'findings', label: t('performance.aiCenter.tabs.findings') },
    { key: 'actions', label: t('performance.aiCenter.tabs.actions') },
    ...(isHr ? [{ key: 'management', label: t('performance.aiCenter.tabs.management') }] : []),
  ];
  const periodOptions = periods.map((p) => ({ id: p.id, name: p.name }));
  const ready = !!periodId;

  return (
    <PageContainer>
      <Header>
        <Breadcrumbs items={[{ label: t('performance.aiCenter.title') }]} />
        <FilterBar>
          <Field>
            <GenericSelect
              label={t('performance.aiCenter.period')}
              value={periodId}
              clearable={false}
              onChange={(v) => setQuery({ period: v })}
              options={periodOptions}
            />
          </Field>
          {tab === 'findings' ? (
            <Field $w="260px">
              <AsyncGenericSelect
                label={t('performance.aiCenter.owner')}
                value={owner}
                compact
                onChange={(v) => setQuery({ owner: v })}
                onSearch={employeeAsync.onSearch}
                onGetById={employeeAsync.onGetById}
              />
            </Field>
          ) : null}
        </FilterBar>
        {periodsLoaded && periods.length === 0 ? <Note data-state="no-periods">{t('performance.aiCenter.noPeriods')}</Note> : null}
        {ready ? (
          <Grid style={{ marginBottom: 12 }}>
            <AiCenterKpis codes={KPI_CODES} metrics={summary.data?.metrics} loading={summary.loading} error={summary.error} onRetry={summary.retry} />
          </Grid>
        ) : null}
        {ready && summary.data ? (
          <Note data-summary="pending">
            {t('performance.aiCenter.pendingNote', { pending: summary.data.pendingReview, stale: summary.data.staleEvidence })}
          </Note>
        ) : null}
        <Tabs tabs={tabs} activeTab={tab} onChange={(k) => setQuery({ tab: k === 'findings' ? null : k })} />
      </Header>
      <TabBody ref={bodyRef}>
        {ready && tab === 'findings' ? (
          <FindingsTab periodId={periodId} ownerEmployeeId={owner} isHr={isHr} containerHeight={bodyHeight} onChanged={onChanged} />
        ) : null}
        {ready && tab === 'actions' ? <ActionsTab periodId={periodId} containerHeight={bodyHeight} refreshKey={refreshKey} /> : null}
        {tab === 'management' && isHr ? <ManagementTab containerHeight={bodyHeight} /> : null}
      </TabBody>
    </PageContainer>
  );
}
